import { useEffect, useRef, useState, type ComponentType } from "react";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import { LogIn, Sparkles, WandSparkles, GitBranch, Route, LogOut, type LucideProps } from "lucide-react";
import ProductMockup from "./ProductMockup";

const ease = [0.22, 1, 0.36, 1] as const;

type IconComp = ComponentType<LucideProps>;

// "AI Solution" uses Lucide's WandSparkles — a pencil/wand with a sparkle
// accent, matching the requested reference and legible at 10–12px.
const AISolution: IconComp = WandSparkles;


type Stage = 0 | 1 | 2 | 3 | 4;
const STAGES = [
  { key: "study", label: "Study" },
  { key: "drag", label: "Drag" },
  { key: "configure", label: "Configure" },
  { key: "package", label: "Package" },
  { key: "live", label: "Live" },
] as const;

// Sequenced walkthrough: docs (0) → build nodes (1) → configure (2) → package loading (3) → live links (4)
const STAGE_DELAYS = [0, 3200, 6000, 9500, 12500];

const paletteItems: Array<{ key: string; label: string; icon: IconComp }> = [
  { key: "inputs", label: "Inputs", icon: LogIn },
  { key: "ai", label: "AI Solution", icon: AISolution },
  { key: "if", label: "If", icon: GitBranch },
  { key: "agent", label: "Agent", icon: Sparkles },
  { key: "output", label: "Output", icon: LogOut },
];

const nodes: Array<{
  id: string;
  label: string;
  x: number;
  y: number;
  w: number;
  paletteIndex: number;
  icon?: IconComp;
}> = [
  { id: "inputs", label: "Stroke imaging", x: 170, y: 120, w: 100, paletteIndex: 0, icon: LogIn },
  { id: "orchestration", label: "Orchestration Agent", x: 170, y: 40, w: 130, paletteIndex: 3, icon: Sparkles },
  { id: "ich", label: "ICH AI Solution", x: 320, y: 40, w: 112, paletteIndex: 1, icon: AISolution },
  { id: "if1", label: "If", x: 455, y: 40, w: 40, paletteIndex: 2, icon: GitBranch },
  { id: "perfusion", label: "Perfusion Analysis", x: 585, y: 40, w: 122, paletteIndex: 1, icon: AISolution },
  { id: "if2", label: "If", x: 620, y: 120, w: 40, paletteIndex: 2, icon: GitBranch },
  { id: "escalation", label: "Escalation Agent", x: 470, y: 150, w: 118, paletteIndex: 3, icon: Sparkles },
  { id: "router", label: "Routing", x: 685, y: 202, w: 78, paletteIndex: 3, icon: Route },
  { id: "pacs", label: "PACS", x: 812, y: 58, w: 90, paletteIndex: 4, icon: LogOut },
  { id: "worklist", label: "Worklist", x: 812, y: 112, w: 90, paletteIndex: 4, icon: LogOut },
  { id: "ehr", label: "EHR", x: 812, y: 166, w: 90, paletteIndex: 4, icon: LogOut },
  { id: "rcm", label: "RCM", x: 812, y: 220, w: 90, paletteIndex: 4, icon: LogOut },
];

const links: Array<[string, string, string?]> = [
  ["inputs", "orchestration"],
  ["orchestration", "ich"],
  ["ich", "if1"],
  ["if1", "escalation", "ICH+"],
  ["if1", "perfusion", "No ICH"],
  ["perfusion", "if2"],
  ["if2", "escalation", "Actionable"],
  ["if2", "router", "Routine"],
  ["escalation", "router"],
  ["router", "pacs"],
  ["router", "worklist"],
  ["router", "ehr"],
  ["router", "rcm"],
];

const nodeById = (id: string) => nodes.find((n) => n.id === id)!;

const docsLines = [
  { tone: "kw", text: "POST /v1/workflows" },
  { tone: "ok", text: "200 OK" },
  { tone: "muted", text: "// Trigger fires on DICOM" },
  { tone: "muted", text: "// stroke imaging received." },
  { tone: "json", text: '{ "trigger": "dicom",' },
  { tone: "json", text: '  "model": "head-ct-ich",' },
  { tone: "json", text: '  "workflow": "stroke-triage" }' },
  { tone: "kw", text: "GET /v1/models" },
  { tone: "muted", text: "// head-ct-ich-v2.1" },
];

const promptText = "Detect suspected intracranial hemorrhage on non-contrast head CT. Return presence, location, confidence, and escalation priority.";

const BuildMockup = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.15, margin: "0px 0px -10% 0px" });
  const reduce = useReducedMotion();
  const [stage, setStage] = useState<Stage>(reduce ? 4 : 0);
  const [docsTyped, setDocsTyped] = useState(0);
  const [promptTyped, setPromptTyped] = useState(0);

  // Stage timeline
  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setStage(4);
      setDocsTyped(docsLines.length);
      setPromptTyped(promptText.length);
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    STAGE_DELAYS.forEach((delay, i) => {
      timers.push(setTimeout(() => setStage(i as Stage), delay));
    });
    return () => timers.forEach(clearTimeout);
  }, [inView, reduce]);

  // Docs typing during stage 0 (gated on inView so it doesn't pre-fill before user sees it)
  useEffect(() => {
    if (reduce) return;
    if (!inView) return;
    if (stage >= 1) {
      setDocsTyped(docsLines.length);
      return;
    }
    setDocsTyped(0);
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      setDocsTyped(Math.min(i, docsLines.length));
      if (i >= docsLines.length) clearInterval(interval);
    }, 280);
    return () => clearInterval(interval);
  }, [stage, reduce, inView]);

  // Prompt typing during configure stage (2)
  useEffect(() => {
    if (reduce) return;
    if (stage < 2) {
      setPromptTyped(0);
      return;
    }
    if (stage > 2) {
      setPromptTyped(promptText.length);
      return;
    }
    let i = 0;
    const interval = setInterval(() => {
      i += 2;
      setPromptTyped(Math.min(i, promptText.length));
      if (i >= promptText.length) clearInterval(interval);
    }, 35);
    return () => clearInterval(interval);
  }, [stage, reduce]);

  // Visibility per stage — one visual per labeled stage
  // 0 Study: docs typing · 1 Drag: nodes drag in · 2 Configure: configure card · 3 Package: docker loading bar · 4 Live: connector lines + ready chip
  const showDocs = stage <= 0;
  const showNodes = stage >= 1;
  const showConfig = stage === 2;
  const showLinks = stage >= 4;
  const showDocker = stage >= 3;
  const dockerReady = stage >= 4;
  const showPackets = stage >= 4;

  return (
    <ProductMockup label="deepc · workflow builder" contentMinHeight="md:min-h-[440px]">
      {/* Stage indicator */}
      <div ref={ref} className="flex items-center gap-1.5 md:gap-3 border-b border-border bg-background/40 px-3 py-2 overflow-x-auto">
        {STAGES.map((s, i) => {
          const active = stage === i;
          const done = stage > i;
          return (
            <div key={s.key} className="flex items-center gap-1.5 md:gap-3 shrink-0">
              <div className="flex items-center gap-1.5">
                <motion.span
                  aria-hidden
                  className={`h-1.5 w-1.5 rounded-full ${done || active ? "bg-primary" : "bg-foreground/20"}`}
                  animate={
                    active && !reduce
                      ? { scale: [1, 1.6, 1], opacity: [1, 0.6, 1] }
                      : { scale: 1, opacity: 1 }
                  }
                  transition={{ duration: 1.4, ease, repeat: active ? Infinity : 0 }}
                />
                <span
                  className={`text-[8.5px] md:text-[10px] uppercase tracking-[0.14em] ${
                    active ? "text-primary" : done ? "text-foreground/70" : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < STAGES.length - 1 && (
                <span className={`h-px w-4 md:w-6 ${done ? "bg-primary/50" : "bg-border"}`} aria-hidden />
              )}
            </div>
          );
        })}
      </div>

      <div className="relative bg-background">
        <svg
          viewBox="0 0 960 320"
          preserveAspectRatio="xMidYMid meet"
          className="hidden md:block w-full h-auto"
          role="img"
          aria-label="Workflow builder: Stroke imaging flows through an Orchestration Agent and ICH AI Solution into a conditional branch. ICH-positive cases route to an Escalation Agent. No-ICH cases route to Perfusion Analysis and a second conditional branch. Actionable perfusion findings route to the Escalation Agent, while routine findings route directly to Routing. Routing fans out to PACS, Worklist, EHR, and RCM."
        >

          <defs>
            <linearGradient id="buildLink" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="hsl(var(--foreground))" stopOpacity="0.25" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.85" />
            </linearGradient>
            <pattern id="buildDots" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="hsl(var(--border))" />
            </pattern>
          </defs>

          {/* Canvas dot grid (right of palette) */}
          <rect x="110" y="0" width="850" height="320" fill="url(#buildDots)" />

          {/* Left rail (palette) */}
          <rect x="0" y="0" width="110" height="320" fill="hsl(var(--muted) / 0.4)" />
          <line x1="110" y1="0" x2="110" y2="320" stroke="hsl(var(--border))" strokeWidth="1" />
          {paletteItems.map((p, i) => (
            <motion.g
              key={p.key}
              initial={{ opacity: 0, x: reduce ? 0 : -4 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.4, ease, delay: 0.05 + i * 0.05 }}
            >
              <rect
                x="13"
                y={20 + i * 56}
                width="84"
                height="40"
                rx="5"
                fill="hsl(var(--card))"
                stroke="hsl(var(--border))"
                strokeWidth="1"
              />
              <g transform={`translate(${22}, ${35 + i * 56})`}>
                <p.icon size={11} color="hsl(var(--foreground))" strokeWidth={1.8} opacity={0.75} />
              </g>
              <text
                x="38"
                y={44 + i * 56}
                fontSize="9"
                fontFamily="Inter, system-ui"
                fill="hsl(var(--foreground))"
                fillOpacity="0.75"
              >
                {p.label}
              </text>
              {/* Lift pulse on the corresponding palette item during drag stage */}
              {stage === 1 && !reduce && (
                <motion.rect
                  x="11"
                  y={18 + i * 56}
                  width="88"
                  height="44"
                  rx="6"
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="1"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.8, 0] }}
                  transition={{ duration: 0.6, ease, delay: i * 0.45 }}
                />
              )}
            </motion.g>
          ))}

          {/* Workflow integration container — sits behind the PACS / Worklist / deepcOS AIR trio */}
          {showNodes && (
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease, delay: 0.2 }}
            >
              <rect
                x="800"
                y="42"
                width="114"
                height="238"
                rx="6"
                fill="none"
                stroke="hsl(var(--border))"
                strokeWidth="1"
              />
              <text
                x="850"
                y="36"
                textAnchor="middle"
                fontSize="8"
                fontFamily="Inter, system-ui"
                fill="hsl(var(--muted-foreground))"
                letterSpacing="0.5"
              >
                Workflow integration
              </text>
            </motion.g>
          )}

          {/* Bezier links (drawn during package+ stages) */}
          {showLinks &&
            links.map(([a, b, edgeLabel], i) => {
              const na = nodeById(a);
              const nb = nodeById(b);
              const verticallyAligned = Math.abs(na.x - nb.x) < 20 && na.y !== nb.y;
              const sourceOverlapsTarget = na.x + na.w > nb.x && na.y !== nb.y;
              let ax: number, ay: number, bx: number, by: number, d: string;
              if (verticallyAligned) {
                const sourceAbove = na.y < nb.y;
                ax = na.x + na.w / 2;
                ay = sourceAbove ? na.y + 36 : na.y;
                bx = nb.x + nb.w / 2;
                by = sourceAbove ? nb.y : nb.y + 36;
                const my = (ay + by) / 2;
                d = `M ${ax} ${ay} C ${ax} ${my}, ${bx} ${my}, ${bx} ${by}`;
              } else if (sourceOverlapsTarget) {
                // Source's right edge sits past target's left edge — exit from source bottom/top, enter target left edge
                const sourceAbove = na.y < nb.y;
                ax = na.x + na.w / 2;
                ay = sourceAbove ? na.y + 36 : na.y;
                bx = nb.x;
                by = nb.y + 18;
                d = `M ${ax} ${ay} C ${ax} ${by}, ${ax} ${by}, ${bx} ${by}`;
              } else {
                ax = na.x + na.w;
                ay = na.y + 18;
                bx = nb.x;
                by = nb.y + 18;
                const mx = (ax + bx) / 2;
                d = `M ${ax} ${ay} C ${mx} ${ay}, ${mx} ${by}, ${bx} ${by}`;
              }
              return (
                <g key={`l-${i}`}>
                  <motion.path
                    id={`build-path-${i}`}
                    d={d}
                    fill="none"
                    stroke="url(#buildLink)"
                    strokeWidth="1"
                    strokeDasharray="2 2"
                    initial={{ opacity: reduce ? 0.7 : 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ duration: 0.5, ease, delay: i * 0.08 }}
                  />
                  {/* Marching-ants dashed line traveling along the path during live stage */}
                  {showPackets && !reduce && (
                    <path
                      d={d}
                      fill="none"
                      stroke="hsl(var(--muted-foreground))"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeDasharray="6 8"
                      style={{ opacity: 0.9 }}
                    >
                      <animate
                        attributeName="stroke-dashoffset"
                        from="14"
                        to="0"
                        dur="0.9s"
                        repeatCount="indefinite"
                      />
                    </path>
                  )}
                  {edgeLabel && (() => {
                    const cx = (ax + bx) / 2;
                    const cy = (ay + by) / 2;
                    const w = edgeLabel.length * 5 + 12;
                    const h = 14;
                    return (
                      <motion.g
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.4, ease, delay: i * 0.08 + 0.5 }}
                      >
                        <rect
                          x={cx - w / 2}
                          y={cy - h / 2}
                          width={w}
                          height={h}
                          rx="3"
                          fill="#ffffff"
                          stroke="hsl(var(--border))"
                          strokeWidth="1"
                        />
                        <text
                          x={cx}
                          y={cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="8"
                          fontFamily="Inter, system-ui"
                          fill="hsl(var(--muted-foreground))"
                        >
                          {edgeLabel}
                        </text>
                      </motion.g>
                    );
                  })()}
                </g>
              );
            })}

          {/* Nodes — animate from palette to canvas position during drag stage */}
          {showNodes &&
            nodes.map((n, i) => {
              const fromX = -(n.x - 13);
              const fromY = -(n.y - (20 + n.paletteIndex * 56));
              const isAgent = n.id === "router";
              return (
                <motion.g
                  key={n.id}
                  initial={reduce ? { opacity: 0 } : { opacity: 0, x: fromX, y: fromY, scale: 0.6 }}
                  animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                  transition={{
                    duration: reduce ? 0.4 : 0.55,
                    ease,
                    delay: reduce ? 0 : 0.05 + i * 0.18,
                    type: reduce ? undefined : "spring",
                    stiffness: 180,
                    damping: 18,
                  }}
                >
                  <rect
                    x={n.x}
                    y={n.y}
                    width={n.w}
                    height="36"
                    rx="5"
                    fill="hsl(var(--card))"
                    stroke="hsl(var(--border))"
                    strokeWidth="1"
                  />
                  {/* Agent alive pulse */}
                  {isAgent && stage >= 4 && !reduce && (
                    <motion.rect
                      x={n.x - 2}
                      y={n.y - 2}
                      width={n.w + 4}
                      height="40"
                      rx="6"
                      fill="none"
                      stroke="hsl(var(--primary))"
                      strokeWidth="1.4"
                      initial={{ opacity: 0.6 }}
                      animate={{ opacity: [0.6, 0.15, 0.6], strokeWidth: [1.4, 2.2, 1.4] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease }}
                    />
                  )}
                  {n.icon && (
                    <g transform={`translate(${n.x + 8}, ${n.y + 12})`}>
                      <n.icon size={12} color="hsl(var(--primary))" strokeWidth={1.8} />
                    </g>
                  )}
                  <text
                    x={n.x + (n.icon ? 24 : 10)}
                    y={n.y + 22}
                    fontSize="10"
                    fontFamily="Inter, system-ui"
                    fill="hsl(var(--foreground))"
                  >
                    {n.label}
                  </text>
                </motion.g>
              );
            })}

          {/* Docker bundle chip — appears during package stage */}
          <AnimatePresence>
            {showDocker && (
              <motion.g
                initial={{ opacity: 0, y: reduce ? 0 : 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease }}
              >
                <rect
                  x="125"
                  y="265"
                  width="200"
                  height="42"
                  rx="6"
                  fill="hsl(var(--background))"
                  stroke="hsl(var(--primary))"
                  strokeWidth="1.4"
                />
                {/* Build progress shimmer */}
                {!dockerReady && !reduce && (
                  <motion.rect
                    x="127"
                    y="300"
                    height="3"
                    rx="1.5"
                    fill="hsl(var(--primary))"
                    initial={{ width: 0 }}
                    animate={{ width: 196 }}
                    transition={{ duration: 1.4, ease }}
                  />
                )}
                {dockerReady && !reduce && (
                  <motion.rect
                    x="125"
                    y="265"
                    width="200"
                    height="42"
                    rx="6"
                    fill="none"
                    stroke="hsl(var(--primary))"
                    strokeWidth="1.4"
                    animate={{ strokeOpacity: [1, 0.35, 1], strokeWidth: [1.4, 2.2, 1.4] }}
                    transition={{ duration: 1.8, ease, repeat: Infinity }}
                  />
                )}
                <circle cx="143" cy="286" r="4" fill="hsl(var(--primary))" />
                <text
                  x="157"
                  y="290"
                  fontSize="10"
                  fontFamily="Inter, system-ui"
                  fill="hsl(var(--foreground))"
                >
                  {dockerReady ? "deepc/stroke-triage:v2.1.0" : "Building bundle…"}
                </text>
              </motion.g>
            )}
          </AnimatePresence>
        </svg>

        {/* Mobile simplified workflow */}
        <div className="md:hidden px-4 py-4 space-y-0">
          {[
            { key: "imaging", title: "Stroke imaging", sub: "Head CT / perfusion data received", activeAt: (s: Stage) => s === 0 },
            { key: "ai", title: "AI analysis", sub: "ICH AI Solution + Perfusion Analysis", activeAt: (s: Stage) => s === 1 || s === 2 },
            { key: "escalation", title: "Escalation Agent", sub: "Actionable findings prioritized", activeAt: (s: Stage) => s === 3 },
            { key: "router", title: "Routing", sub: "PACS · Worklist · EHR · RCM", activeAt: (s: Stage) => s >= 4 },
          ].map((step, i, arr) => {
            const active = step.activeAt(stage);
            return (
              <div key={step.key} className="relative">
                <div
                  className={`flex items-start gap-3 rounded-md border bg-card p-3 transition-colors ${
                    active ? "border-primary/60" : "border-border"
                  }`}
                >
                  <span
                    className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                      active ? "bg-primary" : "bg-foreground/20"
                    }`}
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p
                      className={`text-[10px] uppercase tracking-[0.16em] ${
                        active ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      {step.title}
                    </p>
                    <p className="text-[12px] leading-snug text-foreground/85 mt-0.5 break-words">
                      {step.sub}
                    </p>
                  </div>
                </div>
                {i < arr.length - 1 && (
                  <div className="ml-4 h-4 w-px bg-border" aria-hidden />
                )}
              </div>
            );
          })}
        </div>


        {/* Docs panel (desktop overlay during study/drag) */}
        <AnimatePresence>
          {showDocs && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45, ease }}
              className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] rounded-md border border-border bg-card/95 backdrop-blur-sm p-4 shadow-md"
              aria-hidden
            >
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2.5">
                docs · /v1
              </p>
              <div className="space-y-1 font-mono text-[11px] leading-snug">
                {docsLines.slice(0, docsTyped).map((l, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className={
                      l.tone === "kw"
                        ? "text-primary"
                        : l.tone === "ok"
                        ? "text-primary/70"
                        : l.tone === "json"
                        ? "text-foreground/85"
                        : "text-muted-foreground"
                    }
                  >
                    {l.text}
                  </motion.div>
                ))}
                {stage === 0 && !reduce && (
                  <motion.span
                    className="inline-block h-2 w-1 bg-primary align-middle"
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile status strip for stage 0 */}
        <AnimatePresence>
          {showDocs && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden absolute top-2 right-2 rounded border border-border bg-card/95 px-2 py-1 text-[8px] font-mono text-primary"
              aria-hidden
            >
              {stage === 0 ? "Loading docs…" : "Ready"}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Configure card overlay (desktop) */}
        <AnimatePresence>
          {showConfig && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease }}
              className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] rounded-md border border-primary/40 bg-card shadow-lg p-4"
              aria-hidden
            >
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">
                configure · model
              </p>
              {/* Dropdown */}
              <div className="flex items-center justify-between rounded border border-border bg-background px-3 py-2 mb-3">
                <span className="text-[12px] font-mono text-foreground">head-ct-ich-v2.1</span>
                <motion.span
                  className="text-[12px] text-muted-foreground"
                  initial={{ rotate: 0 }}
                  animate={{ rotate: [0, 180, 0] }}
                  transition={{ duration: 0.7, ease, delay: 0.1 }}
                >
                  ▾
                </motion.span>
              </div>
              {/* Prompt */}
              <div className="rounded border border-border bg-background px-3 py-2 mb-3 min-h-[64px]">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">
                  system prompt
                </p>
                <p className="text-[11px] leading-snug text-foreground/85 font-mono">
                  {promptText.slice(0, promptTyped)}
                  {promptTyped < promptText.length && !reduce && (
                    <span className="inline-block h-2.5 w-[2px] bg-primary align-middle ml-px animate-pulse" />
                  )}
                </p>
              </div>
              {/* Threshold slider */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    threshold
                  </span>
                  <span className="text-[11px] font-mono text-primary">0.82</span>
                </div>
                <div className="relative h-1.5 rounded-full bg-muted">
                  <motion.div
                    className="absolute inset-y-0 left-0 rounded-full bg-primary"
                    initial={{ width: "50%" }}
                    animate={{ width: "82%" }}
                    transition={{ duration: 0.9, ease, delay: 0.4 }}
                  />
                  <motion.div
                    className="absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 border-primary bg-background"
                    initial={{ left: "50%" }}
                    animate={{ left: "82%" }}
                    transition={{ duration: 0.9, ease, delay: 0.4 }}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Configure card (mobile, below SVG) */}
        <AnimatePresence>
          {showConfig && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="md:hidden border-t border-border bg-card/60 p-2.5"
              aria-hidden
            >
              <p className="text-[8px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">
                configure · model
              </p>
              <div className="flex items-center justify-between rounded border border-border bg-background px-2 py-1 mb-1.5">
                <span className="text-[9px] font-mono text-foreground">head-ct-ich-v2.1</span>
                <span className="text-[9px] text-muted-foreground">▾</span>
              </div>
              <p className="text-[9px] leading-snug text-foreground/85 font-mono mb-1.5">
                {promptText.slice(0, promptTyped)}
              </p>
              <div className="flex items-center justify-between text-[8px] uppercase tracking-[0.14em] text-muted-foreground">
                <span>threshold</span>
                <span className="text-primary font-mono">0.82</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Lifecycle bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, ease, delay: 0.3 }}
          className="flex items-center justify-between border-t border-border bg-background/60 px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground"
        >
          <span>workflow · stroke-triage</span>
          <span className="flex items-center gap-2">
            <motion.span
              className={`h-1.5 w-1.5 rounded-full ${stage >= 4 ? "bg-primary" : "bg-foreground/30"}`}
              animate={stage >= 4 && !reduce ? { opacity: [1, 0.4, 1] } : { opacity: 1 }}
              transition={{ duration: 1.4, repeat: Infinity, ease }}
            />
            {stage < 3 ? "drafting…" : stage === 3 ? "packaging…" : "v2.1.0 · live"}
          </span>
        </motion.div>
      </div>
    </ProductMockup>
  );
};

export default BuildMockup;
